/**
 * Authentication utility functions for token management and validation
 */

import type { User } from './types';

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

/**
 * Get token expiration time
 */
export const getTokenExpiration = (token: string): Date | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
};

/**
 * Check if user has superuser permissions
 */
export const isSuperuser = (user: User | null): boolean => {
  return user?.is_superuser ?? false;
};

/**
 * Check if user has staff permissions
 */
export const isStaff = (user: User | null): boolean => {
  return user?.is_staff ?? false;
};

/**
 * Get full name from user object
 */
export const getFullName = (user: User | null): string => {
  if (!user) return '';
  return `${user.first_name} ${user.last_name}`.trim() || user.username;
};

/**
 * Format last login date
 */
export const formatLastLogin = (lastLogin: string | null): string => {
  if (!lastLogin) return 'Never';
  return new Date(lastLogin).toLocaleDateString();
};

/**
 * Check if authentication is required for current route
 */
export const isProtectedRoute = (pathname: string): boolean => {
  const protectedRoutes = ['/dashboard', '/admin', '/profile'];
  return protectedRoutes.some(route => pathname.startsWith(route));
};

/**
 * Get redirect path after login
 */
export const getRedirectPath = (intendedPath?: string): string => {
  if (intendedPath && intendedPath !== '/login' && intendedPath !== '/') {
    return intendedPath;
  }
  return '/dashboard';
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = (): void => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
};

/**
 * Check if tokens exist in localStorage
 */
export const hasStoredTokens = (): boolean => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    return !!(accessToken && refreshToken);
  } catch {
    return false;
  }
};

/**
 * Get stored access token
 */
export const getStoredAccessToken = (): string | null => {
  try {
    return localStorage.getItem('access_token');
  } catch {
    return null;
  }
};

/**
 * Get stored refresh token
 */
export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem('refresh_token');
  } catch {
    return null;
  }
};